import type { GeckoComponentDataset, GeckoEntry } from 'types'

export const CONDUIT_CONFIG_TAG = 'conduit-config'
export const CONDUIT_CONFIG_ENTRY_TYPE = 'conduit-config'
export const MODE_2_MEASUREMENT_LABEL = 'Mode 2'
export const MODE_3_MEASUREMENT_LABEL = 'Mode 3 (BW2-BW1)'
export const MIXED_MEASUREMENT_MODE_LABEL = 'Mixed (Mode 2 / Mode 3)'

type MeasurementGateId = 1 | 2

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function getNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function getMeasurementConfigurations(runConfig: unknown): unknown[] {
  if (Array.isArray(runConfig)) {
    return runConfig
  }

  if (!isRecord(runConfig)) {
    return []
  }

  if (Array.isArray(runConfig.configs)) {
    return runConfig.configs
  }

  return isRecord(runConfig.measurement) ? [runConfig] : []
}

function getGateIdFromMeasurementConfiguration(
  measurementConfiguration: unknown
): MeasurementGateId | undefined {
  if (!isRecord(measurementConfiguration)) {
    return undefined
  }

  const measurement = measurementConfiguration.measurement
  if (!isRecord(measurement)) {
    return undefined
  }

  const measurementType = measurement.measurement_type
  if (measurementType !== 'gates') {
    return undefined
  }

  const thicknessGate = measurement.thickness_gate
  const rawGateId = isRecord(thicknessGate)
    ? getNumber(thicknessGate.gate_id)
    : undefined

  if (rawGateId === 1 || rawGateId === 2) {
    return rawGateId
  }

  return rawGateId === undefined ? 2 : undefined
}

export function getConduitConfigIdsFromDatasets(
  datasets: readonly GeckoComponentDataset[]
): string[] {
  const configIds = new Set<string>()

  datasets.forEach((dataset) => {
    const rawTags = dataset.raw?.tags as UnknownRecord | undefined
    const tags = dataset.tags as UnknownRecord | undefined
    const configId =
      getString(rawTags?.[CONDUIT_CONFIG_TAG]) ??
      getString(tags?.[CONDUIT_CONFIG_TAG])

    if (configId) {
      configIds.add(configId)
    }
  })

  return Array.from(configIds)
}

export function getMeasurementModeLabelFromConduitConfigs(
  conduitConfigs: readonly Pick<GeckoEntry, 'data'>[]
): string | undefined {
  const gateIds = new Set<MeasurementGateId>()

  conduitConfigs.forEach((conduitConfig) => {
    const data = conduitConfig.data
    if (!isRecord(data) || !isRecord(data.measurement_configurations)) {
      return
    }

    Object.values(data.measurement_configurations).forEach((runConfig) => {
      getMeasurementConfigurations(runConfig).forEach(
        (measurementConfiguration) => {
          const gateId = getGateIdFromMeasurementConfiguration(
            measurementConfiguration
          )

          if (gateId) {
            gateIds.add(gateId)
          }
        }
      )
    })
  })

  if (gateIds.size === 0) {
    return undefined
  }

  if (gateIds.size > 1) {
    return MIXED_MEASUREMENT_MODE_LABEL
  }

  return gateIds.has(1) ? MODE_2_MEASUREMENT_LABEL : MODE_3_MEASUREMENT_LABEL
}
